import {
  getSignedWeight,
  type InvestorStrategyRecord,
} from "../services/investorStrategyService";
import type {
  ManagedStrategySearchCompositionEntry,
  ManagedStrategySearchItem,
  ProductSearchItem,
} from "../types/productV3";
import {
  computeGrossPositionCounts,
  getReconciledCurrentWeight,
  type GrossPositionCounts,
} from "./strategyUtils";

// Shared managed-composition rules. These live here rather than under
// pages/alpha so the investor projection and the admin rollup can never drift
// apart on which composition is "current" or which child entries are usable --
// the alpha module imports and re-exports both under their original names.

/**
 * Selects one whole managed composition. A non-empty drift list is current;
 * activated is only a whole-list fallback when drifts are absent or empty. The
 * two arrays are never merged child by child.
 */
export function getManagedCurrentComposition(
  item: ManagedStrategySearchItem
): ManagedStrategySearchCompositionEntry[] | null {
  const snapshot = item.snapshot as unknown as {
    performanceDrifts?: unknown;
    activated?: { composition?: unknown };
  };
  const drifts = snapshot?.performanceDrifts;
  if (Array.isArray(drifts) && drifts.length > 0) {
    return drifts as ManagedStrategySearchCompositionEntry[];
  }
  if (drifts != null && !Array.isArray(drifts)) return null;

  const activated = snapshot?.activated?.composition;
  return Array.isArray(activated)
    ? (activated as ManagedStrategySearchCompositionEntry[])
    : null;
}

export function isValidManagedChild(
  entry: unknown
): entry is ManagedStrategySearchCompositionEntry {
  if (entry === null || typeof entry !== "object") return false;
  const candidate = entry as Partial<ManagedStrategySearchCompositionEntry>;
  return (
    (candidate.entityType === "PORTFOLIO" || candidate.entityType === "BASKET") &&
    Number.isInteger(candidate.productId) &&
    (candidate.productId ?? 0) > 0 &&
    Number.isInteger(candidate.providerId) &&
    (candidate.providerId ?? 0) > 0 &&
    typeof candidate.weight === "number" &&
    Number.isFinite(candidate.weight)
  );
}

/** Reads the activated composition specifically (the allocated side). */
function getManagedActivatedComposition(
  item: ManagedStrategySearchItem
): ManagedStrategySearchCompositionEntry[] | null {
  const snapshot = item.snapshot as unknown as {
    activated?: { composition?: unknown };
  };
  const activated = snapshot?.activated?.composition;
  return Array.isArray(activated)
    ? (activated as ManagedStrategySearchCompositionEntry[])
    : null;
}

export type ProductEntityType = "PORTFOLIO" | "BASKET";

/** One flattened product the account is effectively exposed to. */
export interface RolledUpProductRow {
  entityType: ProductEntityType;
  productId: number;
  displayName: string;
  /** Signed, in percentage points. Sum of every contribution. */
  allocatedWeight: number;
  /**
   * Signed, in percentage points. Null when ANY contribution's current weight
   * is unavailable -- a partial sum would understate the book.
   */
  currentWeight: number | null;
  counts: GrossPositionCounts | null;
  /** False when the product is not in the supplied catalogue. */
  resolved: boolean;
}

export type ManagedRollupIssueCode =
  | "MANAGED_NOT_FOUND"
  | "MANAGED_COMPOSITION_INVALID"
  | "PRODUCT_NOT_FOUND";

export interface ManagedRollupIssue {
  code: ManagedRollupIssueCode;
  sourceKey: string;
  productKey?: string;
}

export interface ManagedRollupResult {
  rows: RolledUpProductRow[];
  issues: ManagedRollupIssue[];
  /**
   * Per managed productId, the summed gross position counts of its children.
   * Null when the strategy or any of its children could not be resolved --
   * a partial count would read as a complete one.
   */
  managedCounts: Map<number, GrossPositionCounts | null>;
}

interface Contribution {
  entityType: ProductEntityType;
  productId: number;
  allocated: number;
  current: number | null;
}

function productKey(entityType: ProductEntityType, productId: number): string {
  return `${entityType}-${productId}`;
}

function sourceKeyOf(record: InvestorStrategyRecord): string {
  return `${record.entityType}-${record.productId}`;
}

/**
 * Direction of a rolled-up row. Derived from the sign of its weight and
 * nothing else -- there is no text position declaration on a rolled-up row,
 * and there must never be one.
 */
export function getRolledUpPosition(row: RolledUpProductRow): "LONG" | "SHORT" {
  const basis = row.allocatedWeight !== 0 ? row.allocatedWeight : row.currentWeight ?? 0;
  return basis < 0 ? "SHORT" : "LONG";
}

/**
 * Flattens a user's investor strategy to the portfolio/basket level: direct
 * holdings pass through, and every managed strategy is expanded into its
 * children at `parent x child / 100`. Contributions to the same product merge
 * into one row.
 *
 * Pure: every input is supplied by the caller, nothing is fetched here.
 */
export function rollUpInvestorStrategyProducts(
  records: InvestorStrategyRecord[],
  managedById: ReadonlyMap<number, ManagedStrategySearchItem>,
  productItems: ProductSearchItem[],
  basketLookup: Map<number, ProductSearchItem>
): ManagedRollupResult {
  const issues: ManagedRollupIssue[] = [];
  const issueKeys = new Set<string>();
  const addIssue = (
    code: ManagedRollupIssueCode,
    sourceKey: string,
    key?: string
  ) => {
    const dedupeKey = `${code}|${sourceKey}|${key ?? ""}`;
    if (issueKeys.has(dedupeKey)) return;
    issueKeys.add(dedupeKey);
    issues.push({ code, sourceKey, ...(key ? { productKey: key } : {}) });
  };

  const itemByKey = new Map<string, ProductSearchItem>();
  for (const item of productItems) {
    itemByKey.set(productKey(item.productType, item.productId), item);
  }

  const contributions: Contribution[] = [];
  const managedChildKeys = new Map<number, string[]>();

  for (const record of records) {
    const allocated = getSignedWeight(record);
    // Reconciled, not raw: an unsigned drift on a short would otherwise flip
    // every child contribution long and cancel against real longs in a merge.
    const current = getReconciledCurrentWeight(record);

    if (record.entityType !== "MANAGED_STRATEGY") {
      contributions.push({
        entityType: record.entityType,
        productId: record.productId,
        allocated,
        current,
      });
      continue;
    }

    const sourceKey = sourceKeyOf(record);
    const managed = managedById.get(record.productId);
    if (
      !managed ||
      managed.productType !== "MANAGED_STRATEGY" ||
      managed.productId !== record.productId
    ) {
      addIssue("MANAGED_NOT_FOUND", sourceKey);
      managedChildKeys.set(record.productId, []);
      continue;
    }

    // A subscriber teaser snapshot is not complete enough for weight math.
    if (managed.snapshot?.partial === true) {
      addIssue("MANAGED_COMPOSITION_INVALID", sourceKey);
      managedChildKeys.set(record.productId, []);
      continue;
    }

    const activated = getManagedActivatedComposition(managed) ?? [];
    const currentChildren = getManagedCurrentComposition(managed) ?? [];
    if (activated.length === 0 && currentChildren.length === 0) {
      addIssue("MANAGED_COMPOSITION_INVALID", sourceKey);
      managedChildKeys.set(record.productId, []);
      continue;
    }

    // Index both sides by product key. A duplicate key inside one composition
    // is a corrupt composition: keep the first and flag it.
    const indexSide = (
      side: ManagedStrategySearchCompositionEntry[]
    ): Map<string, ManagedStrategySearchCompositionEntry> => {
      const map = new Map<string, ManagedStrategySearchCompositionEntry>();
      for (const child of side) {
        if (!isValidManagedChild(child)) {
          addIssue("MANAGED_COMPOSITION_INVALID", sourceKey);
          continue;
        }
        const key = productKey(child.entityType, child.productId);
        if (map.has(key)) {
          addIssue("MANAGED_COMPOSITION_INVALID", sourceKey, key);
          continue;
        }
        map.set(key, child);
      }
      return map;
    };
    const activatedByKey = indexSide(activated);
    const currentByKey = indexSide(currentChildren);

    // Union of both sides: a child that only exists on the drift side is real
    // exposure with no allocation, and must not be dropped.
    const childKeys = new Set<string>([
      ...activatedByKey.keys(),
      ...currentByKey.keys(),
    ]);
    const resolvedChildKeys: string[] = [];

    for (const key of childKeys) {
      const child = activatedByKey.get(key) ?? currentByKey.get(key);
      if (!child) continue;

      // A composition whose providerId disagrees with the catalogue is
      // pointing at a different product than intended. Weighting the wrong
      // product's contracts into a book is worse than reporting the gap.
      const catalogueItem = itemByKey.get(key);
      if (catalogueItem && catalogueItem.providerId !== child.providerId) {
        addIssue("MANAGED_COMPOSITION_INVALID", sourceKey, key);
        continue;
      }

      const activatedChild = activatedByKey.get(key);
      const currentChild = currentByKey.get(key);
      // Both sides are percentage points. Never normalize the composition.
      const childAllocated = activatedChild
        ? (allocated * activatedChild.weight) / 100
        : 0;
      const childCurrent =
        current === null || !currentChild ? null : (current * currentChild.weight) / 100;

      contributions.push({
        entityType: child.entityType,
        productId: child.productId,
        allocated: childAllocated,
        current: childCurrent,
      });
      resolvedChildKeys.push(key);
    }

    managedChildKeys.set(record.productId, resolvedChildKeys);
  }

  // Merge contributions by product.
  const merged = new Map<
    string,
    {
      entityType: ProductEntityType;
      productId: number;
      allocatedWeight: number;
      currentWeight: number | null;
    }
  >();
  for (const contribution of contributions) {
    const key = productKey(contribution.entityType, contribution.productId);
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, {
        entityType: contribution.entityType,
        productId: contribution.productId,
        allocatedWeight: contribution.allocated,
        currentWeight: contribution.current,
      });
      continue;
    }
    existing.allocatedWeight += contribution.allocated;
    // One unavailable current makes the whole merged current unavailable.
    existing.currentWeight =
      existing.currentWeight === null || contribution.current === null
        ? null
        : existing.currentWeight + contribution.current;
  }

  const countsCache = new Map<string, GrossPositionCounts | null>();
  const countsFor = (key: string): GrossPositionCounts | null => {
    if (countsCache.has(key)) return countsCache.get(key) ?? null;
    const item = itemByKey.get(key);
    const counts = item ? computeGrossPositionCounts(item, basketLookup) : null;
    countsCache.set(key, counts);
    return counts;
  };

  const rows: RolledUpProductRow[] = [];
  for (const [key, entry] of merged) {
    if (entry.allocatedWeight === 0 && entry.currentWeight === 0) continue;
    const item = itemByKey.get(key);
    if (!item) addIssue("PRODUCT_NOT_FOUND", key, key);
    rows.push({
      entityType: entry.entityType,
      productId: entry.productId,
      displayName: item?.title ?? `${entry.entityType} #${entry.productId}`,
      allocatedWeight: entry.allocatedWeight,
      currentWeight: entry.currentWeight,
      counts: countsFor(key),
      resolved: Boolean(item),
    });
  }

  rows.sort((a, b) => {
    const byWeight = Math.abs(b.allocatedWeight) - Math.abs(a.allocatedWeight);
    if (byWeight !== 0) return byWeight;
    if (a.entityType !== b.entityType) return a.entityType.localeCompare(b.entityType);
    return a.productId - b.productId;
  });

  // Per managed strategy: summed child counts, or null when anything under it
  // is unresolved.
  const managedCounts = new Map<number, GrossPositionCounts | null>();
  for (const [managedId, childKeys] of managedChildKeys) {
    if (childKeys.length === 0) {
      managedCounts.set(managedId, null);
      continue;
    }
    let total: GrossPositionCounts | null = { long: 0, short: 0, options: 0 };
    for (const key of childKeys) {
      const counts = countsFor(key);
      if (!counts) {
        total = null;
        break;
      }
      total = {
        long: total.long + counts.long,
        short: total.short + counts.short,
        options: total.options + counts.options,
      };
    }
    managedCounts.set(managedId, total);
  }

  return { rows, issues, managedCounts };
}

/** Totals for the rolled-up table footer. */
export function sumRolledUpWeights(rows: RolledUpProductRow[]): {
  allocated: number;
  current: number | null;
} {
  let allocated = 0;
  let current: number | null = 0;
  for (const row of rows) {
    allocated += row.allocatedWeight;
    if (current === null || row.currentWeight === null) {
      current = null;
      continue;
    }
    current += row.currentWeight;
  }
  return { allocated, current };
}
